/********************* Setup *********************/

// live cart (declared globally in theme.liquid) + per-request supplemental item data
let cartData = {};

// the layout puts a b2b class on the html tag for Professional/Distributor customers
const IS_B2B = document.documentElement.classList.contains("b2b");

// has the shopper seen the cart this session? (used by other scripts via the cartViewed event)
window.hasViewedCart = sessionStorage.getItem("hasViewedCart") === "true";

// parse a settings textarea (one value per line) into a trimmed, non-empty array
function parseList(raw) {
    if (!raw) {
        return [];
    }
    return raw.split("\n").map(value => value.trim()).filter(Boolean);
}

// free gift / shipping; thresholds come from placeholder products, so they arrive already converted and rounded
// into the shopper's presentment currency and can be compared to cart.total_price directly
const FREE_SHIPPING_THRESHOLD = cartSettings.free_shipping_threshold_price || 0;
const FREE_PRODUCT_THRESHOLD = cartSettings.free_product_threshold_price || 0;
const FREE_PRODUCT = cartSettings.free_product;
const FREE_GIFT = cartSettings.free_gift;

// markets that get the free gift + the gift status bar
const GIFT_ENABLED_COUNTRIES = ["US", "CA"];
const IS_GIFT_ENABLED_COUNTRY = GIFT_ENABLED_COUNTRIES.includes(Shopify.country);

// cart logic SKU policies (fixed bundle SKUs are also anti-upgrade)
const FIXED_BUNDLE_SKUS = parseList(cartSettings.fixed_bundle_skus);
const ANTI_GIFT_SKUS = parseList(cartSettings.anti_gift_skus);
const ANTI_UPGRADE_SKUS = [...parseList(cartSettings.anti_upgrade_skus), ...FIXED_BUNDLE_SKUS];
const ANTI_HEADER_SKUS = parseList(cartSettings.anti_header_skus);

// products/variants excluded from the subscribe-upgrade button
const UPGRADE_EXCLUDED_PRODUCT_IDS = parseList(cartSettings.upgrade_excluded_product_ids).map(Number);
const UPGRADE_EXCLUDED_VARIANT_IDS = parseList(cartSettings.subscribe_upgrade_excluded_variant_ids).map(Number);

// upsell selection
const DEFAULT_UPSELL_SKU = cartSettings.default_upsell_sku;
const BUNDLE_UPSELL_SKUS = parseList(cartSettings.bundle_upsell_skus);
const MOUTHWASH_4PACK_UPSELL_SKU = cartSettings.mouthwash_4pack_upsell_sku;
const MOUTHWASH_4PACK_PRODUCT_IDS = parseList(cartSettings.mouthwash_4pack_product_ids).map(Number);

// per-month pricing: map "<productId>_<variantId>" or "<productId>" or "<variantId>" to a divisor
const PER_MONTH_TEXT = cartSettings.custom_per_month_text;
const perMonthMapping = {};
parseList(cartSettings.custom_per_month_products).forEach(line => {
    const parts = line.split(":");
    if (parts.length === 3) {
        perMonthMapping[`${parts[0].trim()}_${parts[1].trim()}`] = parseFloat(parts[2].trim());
    } else if (parts.length === 2) {
        perMonthMapping[parts[0].trim()] = parseFloat(parts[1].trim());
    }
});

// upsell carousel; rebuilt from the cartUpsells pool on every render
let upsellsSwiper = null;

// re-entry lock so rapid quantity clicks don't fire overlapping gift syncs against the same cart
let syncingFreeGifts = false;

// countdown interval handle
let timer = null;

/********************* Render *********************/

// open the cart: fetch fresh state, reconcile gifts, then paint
async function openCart() {
    // open the dialog right away and show the loading overlay; it also blocks clicks while we work
    if (document.querySelector("#miniCart:not([open])")) {
        document.querySelector("#miniCart").showModal();
    }
    document.querySelector("body").classList.add("noScroll");
    document.querySelector("#miniCartContent").classList.add("loading");
    markCartAsViewed();

    // fetch live cart and per-request supplemental data in parallel
    const [liveCart, supplemental] = await Promise.all([
        fetch("/cart.js").then(response => response.json()),
        fetch("/cart?view=supplemental-data").then(response => response.json()).catch(() => null),
    ]);
    cart = liveCart;
    // fall back to an empty map if the supplemental view fails
    cartData = supplemental || {};

    // bail if the cart API failed or rate-limited
    if (!cart || !cart.items) {
        document.querySelector("#miniCartContent").classList.remove("loading");
        return;
    }


    // strip shipping protection items, then re-open so the rest of the render sees a clean cart
    const protectionItems = cart.items.filter(item => item.handle === "shipping-protection-1");
    if (protectionItems.length > 0) {
        const updates = {};
        protectionItems.forEach(item => {
            updates[item.key] = 0;
        });
        const response = await updateCart({updates});
        if (response.items) {
            cart = response;
        }
        await openCart();
        return;
    }

    // remove/adjust free gifts based on the real cart total; re-open if anything changed
    const giftsChanged = await syncFreeGifts();
    if (giftsChanged) {
        await openCart();
        return;
    }

    // reset the body and hide everything b2b-excluded; the non-b2b paths below re-activate what applies
    document.querySelector("#miniCartBody").innerHTML = "";
    document.querySelector("#miniCartHeaderStatus").classList.remove("active");
    document.querySelector("#miniCartUpsells").classList.remove("active");
    document.querySelector("#miniCartUpsellsFree").classList.remove("active");
    document.querySelector("#miniCartUpsellsFreeItem").classList.remove("active");
    document.querySelector("#miniCartUpsellsGiftItem").classList.remove("active");
    document.querySelector("#headerCart").setAttribute("data-count", cart.item_count);

    // empty cart: show the empty state and a starter upsell, then exit
    if (cart.item_count === 0) {
        document.querySelector("#miniCartBody").classList.remove("active");
        document.querySelector("#miniCartEmpty").classList.add("active");
        document.querySelector("#miniCartHeaderExpires").classList.remove("active");
        document.querySelector("#miniCartFooter").classList.remove("active");

        if (!IS_B2B) {
            document.querySelector("#miniCartUpsellsTitle").textContent = "Or get started here";
            renderUpsells();
        }

        document.querySelector("#miniCartContent").classList.remove("loading");
        return;
    }

    // if only free gifts remain (last paid item was removed), clear the cart and re-open
    if (cart.items.every(item => item.properties?._free)) {
        await clearCart();
        await openCart();
        return;
    }

    // base state for a non-empty cart
    document.querySelector("#miniCartHeaderExpires").classList.add("active");
    document.querySelector("#miniCartBody").classList.add("active");
    document.querySelector("#miniCartEmpty").classList.remove("active");
    document.querySelector("#miniCartFooter").classList.add("active");
    document.querySelector("#miniCartHeaderGift").classList.remove("active");

    // show the free gift message unless an anti-gift SKU is present
    if (!cart.items.some(item => ANTI_GIFT_SKUS.includes(item.sku))) {
        document.querySelector("#miniCartHeaderGift").classList.add("active");
    }

    // build the line items as one HTML string, then paint once
    let bodyHTML = "";
    cart.items.forEach(item => {
        const supplement = cartData[item.key] || {};

        // variant title
        let variantHTML = "";
        if (item.variant_title) {
            variantHTML = `<div class="miniCartBodyItemVariant">${item.variant_title}</div>`;
        }

        // subscription name
        let subscriptionHTML = "";
        if (item.selling_plan_allocation) {
            subscriptionHTML = `<div class="miniCartBodyItemSellingPlan">${item.selling_plan_allocation.selling_plan.name}</div>`;
        }

        // quantity stepper: standard by default, overridden by SKU policy
        let quantityHTML = `
            <div class="miniCartBodyItemQuantity active">
                <button class="miniCartBodyItemQuantityMinus">-</button>
                <div class="miniCartBodyItemQuantityCount">${item.quantity}</div>
                <button class="miniCartBodyItemQuantityPlus">+</button>
            </div>
        `;
        if (item.properties?._free) {
            // free gift line items have no quantity controls
            quantityHTML = "";

        } else if (FIXED_BUNDLE_SKUS.includes(item.sku) || item.properties?._bundle) {
            // fixed bundles and bundle builder items have no quantity controls but show quantity
            quantityHTML = `
                <div class="miniCartBodyItemQuantity active">
                    <div class="miniCartBodyItemQuantityCount noWrap">Quantity: ${item.quantity}</div>
                </div>
            `;
            
        } else if (supplement.is_free_product || item.sku === "LIV4488-1X") {
            // free-collection products and LIV4488-1X render the stepper but keep it hidden (no active class)
            quantityHTML = `
                <div class="miniCartBodyItemQuantity">
                    <button class="miniCartBodyItemQuantityMinus">-</button>
                    <div class="miniCartBodyItemQuantityCount">${item.quantity}</div>
                    <button class="miniCartBodyItemQuantityPlus">+</button>
                </div>
            `;
        }

        // per-month price
        let perMonthHTML = "";
        const variantId = item.variant_id || item.id;
        const divisor = perMonthMapping[`${item.product_id}_${variantId}`]
            || perMonthMapping[item.product_id]
            || perMonthMapping[item.product_id?.toString()]
            || perMonthMapping[variantId]
            || perMonthMapping[variantId?.toString()];
        if (divisor) {
            const perMonthValue = price(item.final_line_price / divisor);
            let perMonthText = `${perMonthValue} Per Month`;
            if (PER_MONTH_TEXT) {
                perMonthText = PER_MONTH_TEXT.replace("[value]", perMonthValue);
            }
            perMonthHTML = `<div class="miniCartBodyItemPerMonth">${perMonthText}</div>`;
        }

        // free gift line items get the free state class, which hides the remove + upgrade buttons
        let freeClass = "";
        if (item.properties?._free) {
            freeClass = " free";
        }

        // per-month items mark the price line and add "due today"
        let priceOriginalModifier = "";
        let dueTodayText = "";
        if (perMonthHTML) {
            priceOriginalModifier = " hasPerMonth";
            dueTodayText = "due today";
        }

        // struck-through original price when discounted
        let compareHTML = "";
        if (item.original_line_price > item.final_line_price) {
            compareHTML = price(item.original_line_price);
        }

        // final price, or FREE at zero
        let finalPriceText = price(item.final_line_price);
        if (item.final_line_price === 0) {
            finalPriceText = "FREE";
        }

        bodyHTML += `
            <div class="miniCartBodyItem${freeClass}" data-key="${item.key}">
                <img class="miniCartBodyItemImage" src="${item.image}&width=200">
                <div class="miniCartBodyItemColumn">
                    <div class="miniCartBodyItemTitle">${item.product_title}</div>
                    ${variantHTML}
                    ${subscriptionHTML}
                    ${quantityHTML}
                </div>
                <div class="miniCartBodyItemColumn">
                    <button class="miniCartBodyItemRemove">
                        <svg version="1.1" class="miniCartBodyItemRemoveIcon" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 375 516.7" style="enable-background:new 0 0 375 516.7;" xml:space="preserve"><style type="text/css">.st0{fill:#A7AAAC;}</style><path class="st0" d="M151.2,252c-9.6-9.9-25.5-10.1-35.4-0.4c-9.9,9.6-10.1,25.5-0.4,35.4L151.2,252zM169.6,342.5c9.6,9.9,25.5,10.1,35.4,0.4c9.9-9.6,10.1-25.5,0.4-35.4L169.6,342.5zM115.4,363.1c-9.6,9.9-9.4,25.7,0.4,35.4c9.9,9.6,25.7,9.4,35.4-0.4L115.4,363.1zM205.4,342.5c9.6-9.9,9.4-25.7-0.4-35.4c-9.9-9.6-25.7-9.4-35.4,0.4L205.4,342.5zM259.6,286.9c9.6-9.9,9.4-25.7-0.4-35.4c-9.9-9.6-25.7-9.4-35.4,0.4L259.6,286.9zM169.6,307.5c-9.6,9.9-9.4,25.7,0.4,35.4c9.9,9.6,25.7,9.4,35.4-0.4L169.6,307.5zM223.8,398c9.6,9.9,25.5,10.1,35.4,0.4c9.9-9.6,10.1-25.5,0.4-35.4L223.8,398zM205.4,307.5c-9.6-9.9-25.5-10.1-35.4-0.4c-9.9,9.6-10.1,25.5-0.4,35.4L205.4,307.5zM244.8,50c13.8,0,25-11.2,25-25s-11.2-25-25-25V50zM136.4,0c-13.8,0-25,11.2-25,25s11.2,25,25,25V0zM350,116.7c13.8,0,25-11.2,25-25s-11.2-25-25-25V116.7zM25,66.7c-13.8,0-25,11.2-25,25s11.2,25,25,25V66.7zM48.8,467.3l-17.9,17.5l0,0L48.8,467.3z M25,408.3h25H25zM115.4,286.9l54.2,55.6l35.8-34.9L151.2,252L115.4,286.9zM151.2,398l54.2-55.6l-35.8-34.9l-54.2,55.6L151.2,398zM223.8,252l-54.2,55.6l35.8,34.9l54.2-55.6L223.8,252zM259.6,363.1l-54.2-55.6l-35.8,34.9l54.2,55.6L259.6,363.1zM244.8,0H136.4v50h108.3V0zM350,66.7H25v50h325V66.7zM52.1,183.3h270.9v-50H52.1V183.3zM322.9,183.3c0.4,0,0.8,0.1,1.2,0.6c0.5,0.5,0.8,1.2,0.8,2.2h50c0-28.5-22.7-52.8-52.1-52.8V183.3zM325,186.1v222.2h50V186.1H325zM325,408.3c0,32.8-25.8,58.3-56.2,58.3v50c59.3,0,106.3-49.1,106.3-108.3H325zM268.7,466.7H106.3v50h162.5V466.7zM106.3,466.7c-14.7,0-29-6-39.6-16.9l-35.8,34.9c19.9,20.4,47,32,75.4,32V466.7zM66.7,449.8C56.1,438.9,50,424,50,408.3H0c0,28.5,11,56,30.9,76.4L66.7,449.8zM50,408.3V186.1H0v222.2H50zM50,186.1c0-1,0.4-1.7,0.8-2.2c0.5-0.5,0.9-0.6,1.2-0.6v-50C22.7,133.3,0,157.6,0,186.1H50z"/></svg>
                    </button>
                    <div class="miniCartBodyItemPrice">
                        ${perMonthHTML}
                        <div class="miniCartBodyItemPriceOriginal${priceOriginalModifier}">
                            <s class="miniCartBodyItemPriceStrike">${compareHTML}</s>
                            ${finalPriceText} ${dueTodayText}
                        </div>
                    </div>
                </div>
                <button class="miniCartBodyItemUpgrade button"> Subscribe Instead</button>
            </div>
        `;
    });
    document.querySelector("#miniCartBody").innerHTML = bodyHTML;

    // wire each line item's quantity / remove / upgrade controls
    document.querySelectorAll(".miniCartBodyItem").forEach(bindLineItem);

    // b2b carts stop here: the status bar, gifts and upsells stay hidden from the reset above
    if (IS_B2B) {
        document.querySelector("#miniCartContent").classList.remove("loading");

        if (window.Fondue?.status === "ready") {
            await window.Fondue.cart.dispatchUpdatedCartEvent();
        }
        return;
    }

    // show the free gift display item
    document.querySelector("#miniCartUpsellsFree").classList.add("active");

    // show the velvet bag display item; the checkout extension adds the real line, so it follows the same anti-gift rule.
    // a shopper back from checkout already has the real line, which renders in the body instead
    const antiGiftInCart = cart.items.some(item => ANTI_GIFT_SKUS.includes(item.sku));
    const freeGiftInCart = cart.items.some(item => item.properties?._free && item.variant_id === FREE_GIFT?.variants?.[0]?.id);
    if (!antiGiftInCart && !freeGiftInCart) {
        document.querySelector("#miniCartUpsellsGiftItem").classList.add("active");
    }

    // header status bar + free shipping / free product progress (gift-enabled countries only)
    renderStatusBar();

    // anti-header SKUs hide the status bar
    if (ANTI_HEADER_SKUS.some(sku => cart.items.some(item => item.sku === sku))) {
        document.querySelector("#miniCartHeaderStatus").classList.remove("active");
    }

    // refresh the upsell carousel and the subscribe-upgrade buttons
    renderUpsells();
    renderUpgradeButtons();

    // footer gift banner shows in gift-enabled countries only
    if (IS_GIFT_ENABLED_COUNTRY) {
        document.querySelector("#miniCartFooterGift").classList.add("active");
    } else {
        document.querySelector("#miniCartFooterGift").classList.remove("active");
    }

    document.querySelector("#miniCartContent").classList.remove("loading");

    if (window.Fondue?.status === "ready") {
        await window.Fondue.cart.dispatchUpdatedCartEvent();
    }
}

// render the header status bar (free shipping + free product progress); gift-enabled countries only
function renderStatusBar() {
    // a threshold of 0 means its placeholder product did not resolve, so there is nothing to progress toward
    const status = document.querySelector("#miniCartHeaderStatus");
    if (!IS_GIFT_ENABLED_COUNTRY || !FREE_SHIPPING_THRESHOLD || !FREE_PRODUCT_THRESHOLD) {
        status.classList.remove("active");
        return;
    }

    status.classList.add("active");
    document.querySelector("#miniCartUpsellsTitle").textContent = "Want More? Here Are Some Best Sellers!";

    const freeShippingLabel = document.querySelector("#miniCartHeaderStatusFreeShipping").getAttribute("label");
    const freeProductLabel = document.querySelector("#miniCartHeaderStatusFreeProduct").getAttribute("label");

    // status message
    let statusTitleHTML = "Enjoy free shipping and your free gift!";
    if (cart.total_price < FREE_SHIPPING_THRESHOLD) {
        statusTitleHTML = `You are <b class="miniCartHeaderStatusTitleBold">${price(FREE_SHIPPING_THRESHOLD - cart.total_price)}</b> away from <b class="miniCartHeaderStatusTitleBold">${freeShippingLabel}!</b>`;
    } else if (cart.total_price < FREE_PRODUCT_THRESHOLD) {
        statusTitleHTML = `You are <b class="miniCartHeaderStatusTitleBold">${price(FREE_PRODUCT_THRESHOLD - cart.total_price)}</b> away from <b class="miniCartHeaderStatusTitleBold">${freeProductLabel}!</b>`;
    } else if (ANTI_GIFT_SKUS.some(sku => cart.items.some(item => item.sku === sku))) {
        statusTitleHTML = "Enjoy free shipping!";
    }
    document.querySelector("#miniCartHeaderStatusTitle").innerHTML = statusTitleHTML;

    // progress segments (segment 0 is always full; we drive 1, 2 and 3)
    const segments = status.querySelectorAll(".miniCartHeaderStatusBarProgress");
    if (cart.total_price < FREE_SHIPPING_THRESHOLD) {
        setProgressWidth(segments[1], `${cart.total_price / FREE_SHIPPING_THRESHOLD * 100}%`);
        setProgressWidth(segments[2], "0%");
        setProgressWidth(segments[3], "0%");
    } else if (cart.total_price === FREE_SHIPPING_THRESHOLD) {
        setProgressWidth(segments[1], "100%");
        setProgressWidth(segments[2], "0%");
        setProgressWidth(segments[3], "0%");
    } else if (cart.total_price < FREE_PRODUCT_THRESHOLD) {
        setProgressWidth(segments[1], "100%");
        setProgressWidth(segments[2], "50%");
        setProgressWidth(segments[3], "0%");
    } else {
        setProgressWidth(segments[1], "100%");
        setProgressWidth(segments[2], "100%");
        setProgressWidth(segments[3], "100%");
    }

    // free shipping circle
    if (cart.total_price >= FREE_SHIPPING_THRESHOLD) {
        document.querySelector("#miniCartHeaderStatusFreeShipping").classList.add("active");
    } else {
        document.querySelector("#miniCartHeaderStatusFreeShipping").classList.remove("active");
    }

    // free product circle + the free display item
    if (cart.total_price >= FREE_PRODUCT_THRESHOLD) {
        document.querySelector("#miniCartHeaderStatusFreeProduct").classList.add("active");

        // show the free display item, except for anti-header SKUs
        if (ANTI_HEADER_SKUS.some(sku => cart.items.some(item => item.sku === sku))) {
            document.querySelector("#miniCartUpsellsFreeItem").classList.remove("active");
        } else {
            document.querySelector("#miniCartUpsellsFreeItem").classList.add("active");
        }
    } else {
        document.querySelector("#miniCartHeaderStatusFreeProduct").classList.remove("active");
    }
}

// set a progress segment's inner fill width
function setProgressWidth(segment, width) {
    if (!segment) {
        return;
    }
    segment.querySelector(".miniCartHeaderStatusBarProgressInner").style.width = width;
}

// flag the cart as viewed and notify listeners
function markCartAsViewed() {
    window.hasViewedCart = true;
    sessionStorage.setItem("hasViewedCart", "true");
    document.dispatchEvent(new CustomEvent("cartViewed"));
}

/********************* Line Item Events *********************/

// wire one line item's quantity / remove / upgrade controls
function bindLineItem(element) {
    const key = element.dataset.key;
    let quantity = parseInt(element.querySelector(".miniCartBodyItemQuantityCount")?.textContent);

    // decrement (or remove at 1)
    element.querySelector(".miniCartBodyItemQuantityMinus")?.addEventListener("click", function () {
        if (quantity === 1) {
            element.querySelector(".miniCartBodyItemRemove").click();
            return;
        }
        quantity--;
        updateQuantity();
    });

    // increment
    element.querySelector(".miniCartBodyItemQuantityPlus")?.addEventListener("click", function () {
        quantity++;
        updateQuantity();
    });

    // remove (fade out, then drop)
    element.querySelector(".miniCartBodyItemRemove")?.addEventListener("click", function () {
        quantity = 0;
        updateQuantity();
        element.classList.add("fade");
        setTimeout(() => element.remove(), 500);
    });

    // subscribe-upgrade
    element.querySelector(".miniCartBodyItemUpgrade")?.addEventListener("click", () => upgradeItem(element));

    // push the new quantity to Shopify, then re-render; loading blocks further clicks until done
    async function updateQuantity() {
        document.querySelector("#miniCartContent").classList.add("loading");

        // fixed bundles and free items render no stepper, so the count label may not exist
        const countElement = element.querySelector(".miniCartBodyItemQuantityCount");
        if (countElement) {
            countElement.textContent = quantity;
        }

        cart = await changeProduct({id: key, quantity: quantity});
        await openCart();
    }
}

// swap a one-time line for its subscription counterpart
async function upgradeItem(element) {
    const upgradeButton = element.querySelector(".miniCartBodyItemUpgrade");
    upgradeButton.classList.add("loading");
    document.querySelector("#miniCartContent").classList.add("loading");

    const key = element.dataset.key;
    const cartItem = cart.items.find(item => item.key === key);
    const upgrade = (cartData[key] || {}).upgrade;

    // bail if there is no resolved subscription counterpart
    if (!cartItem || !upgrade) {
        upgradeButton.classList.remove("loading");
        document.querySelector("#miniCartContent").classList.remove("loading");
        return;
    }

    // remove the one-time line first (its key can shift as the cart changes)
    await changeProduct({id: key, quantity: 0});

    // add the subscription counterpart, tagged for upsell tracking
    await addProduct({
        items: [{
            quantity: cartItem.quantity,
            id: upgrade.variant_id,
            selling_plan: upgrade.selling_plan_id,
            properties: {_upsell: true},
        }],
    });

    upgradeButton.classList.remove("loading");
    await openCart();
}

/********************* Upsells *********************/

// find a smaller pack of the same product (same prefix, same one-time/subscription letter) in the cart
function findSmallerPackInCart(sku) {
    const [prefix, suffix] = sku.toLowerCase().split("-");
    return cart.items.find(item => {
        const [itemPrefix, itemSuffix] = item.sku.toLowerCase().split("-");
        return itemPrefix === prefix && parseInt(itemSuffix[0]) < parseInt(suffix[0]) && itemSuffix[1] === suffix[1];
    });
}

// pick the upsells for this cart and rebuild the carousel from the cartUpsells pool
function renderUpsells() {
    // default upsell, overridden when a bundle, a mouthwash 4-pack, or an affiliate product is in the cart
    let upsellSKUs = [DEFAULT_UPSELL_SKU];
    if (cart.items.some(item => item.sku.split("-")[0].toLowerCase() === "liv6621")) {
        upsellSKUs = BUNDLE_UPSELL_SKUS;
    } else if (cart.items.some(item => MOUTHWASH_4PACK_PRODUCT_IDS.includes(item.product_id))) {
        upsellSKUs = [MOUTHWASH_4PACK_UPSELL_SKU];
        // wintergreen affiliate pages (LIV1153-AF), upsell whitening
    } else if (cart.items.some(item => item.sku.toLowerCase().startsWith("liv1153-af"))) {
        upsellSKUs = ["liv5522-af-1x"];
        // whitening strips affiliate pages (LIV5522-AF) upsell wintergreen
    } else if (cart.items.some(item => item.sku.toLowerCase().startsWith("liv5522-af"))) {
        upsellSKUs = ["liv1153-af-1x"];
    }

    // map the chosen SKUs to pool entries, dropping anything already in the cart
    const cartProductIDs = cart.items.map(item => item.product_id);
    let availableUpsells = upsellSKUs
        .map(sku => cartUpsells.find(upsell => upsell.sku === sku))
        .filter(Boolean)
        .filter(upsell => !cartProductIDs.includes(upsell.product_id));

    // when every chosen upsell is in the cart, fall back to the first pool upsell that isn't
    if (availableUpsells.length === 0) {
        availableUpsells = cartUpsells.filter(upsell => !cartProductIDs.includes(upsell.product_id)).slice(0, 1);
    }

    // tear down the previous swiper so each render rebuilds from the current upsells
    if (upsellsSwiper) {
        upsellsSwiper.destroy(true, true);
        upsellsSwiper = null;
    }

    // hide the whole carousel when every pool upsell is already in the cart
    if (availableUpsells.length === 0) {
        document.querySelector("#miniCartUpsells").classList.remove("active");
        return;
    }
    document.querySelector("#miniCartUpsells").classList.add("active");

    // build the slides as one html string
    let upsellsHTML = "";
    availableUpsells.forEach(upsell => {
        // compare-at price is optional and marks the price as discounted
        let priceModifier = "";
        let comparePriceHTML = "";
        if (upsell.compare_at_price > 0) {
            priceModifier = " active";
            comparePriceHTML = `<div class="miniCartUpsellsItemWrapBodyPriceTotalOG">${price(upsell.compare_at_price)}</div>`;
        }

        // label: Upgrade when a smaller pack of the same product is in the cart, SOLD OUT when unavailable
        let label = "Add";
        let disabledAttribute = "";
        if (findSmallerPackInCart(upsell.sku)) {
            label = "Upgrade";
        }
        if (!upsell.available) {
            label = "SOLD OUT";
            disabledAttribute = " disabled";
        }

        upsellsHTML += `
            <div class="miniCartUpsellsItem swiper-slide" data-sku="${upsell.sku}">
                <div class="miniCartUpsellsItemWrap">
                    <img class="miniCartUpsellsItemWrapImg" src="${upsell.image}" alt="${upsell.title}">
                    <div class="miniCartUpsellsItemWrapBody">
                        <div class="miniCartUpsellsItemWrapBodyTitle">${upsell.title}</div>
                        <div class="miniCartUpsellsItemWrapBodyPrice">
                            <div class="miniCartUpsellsItemWrapBodyPriceTotal${priceModifier}">${price(upsell.price)}</div>
                            ${comparePriceHTML}
                        </div>
                    </div>
                    <button class="miniCartUpsellsItemWrapATC button"${disabledAttribute}>${label}</button>
                </div>
            </div>
        `;
    });
    document.querySelector("#miniCartUpsellsSwiperWrap").innerHTML = upsellsHTML;

    // init the carousel against the existing prev/next controls
    upsellsSwiper = new Swiper("#miniCartUpsellsSwiper", {
        slidesPerView: 1,
        spaceBetween: 100,
        navigation: {
            nextEl: ".miniCartUpsellsSwiperNext",
            prevEl: ".miniCartUpsellsSwiperPrev",
        },
    });

    // bind the add buttons (slides are rebuilt every render)
    document.querySelectorAll(".miniCartUpsellsItemWrapATC").forEach(button => {
        button.addEventListener("click", () => addUpsellToCart(button));
    });
}

// add an upsell to the cart, swapping out a smaller pack of the same product if present
async function addUpsellToCart(button) {
    button.classList.add("loading");
    document.querySelector("#miniCartContent").classList.add("loading");

    const sku = button.closest(".miniCartUpsellsItem").dataset.sku;
    const upsell = cartUpsells.find(entry => entry.sku === sku);

    // a smaller pack of the same product makes this an upgrade: swap it out after adding
    const smallerPack = findSmallerPackInCart(sku);

    // add the upsell, tagged for tracking, with its selling plan when present
    const formData = {
        items: [{
            quantity: 1,
            id: upsell.variant_id,
            properties: {_upsell: true},
        }],
    };
    if (upsell.selling_plan_id) {
        formData.items[0].selling_plan = upsell.selling_plan_id;
    }
    await addProduct(formData);

    // remove the smaller pack we just replaced
    if (smallerPack) {
        await changeProduct({id: smallerPack.key, quantity: 0});
    }

    await openCart();
}

// show the subscribe-upgrade button on eligible one-time line items, with the savings amount
function renderUpgradeButtons() {
    cart.items.forEach(item => {
        if (!item.sku.includes("-")) {
            return;
        }

        const supplement = cartData[item.key] || {};
        if (supplement.is_free_product) {
            return;
        }
        // without a resolved subscription counterpart the upgrade click has nothing to swap to
        if (!supplement.upgrade) {
            return;
        }
        if (UPGRADE_EXCLUDED_PRODUCT_IDS.includes(item.product_id)) {
            return;
        }
        // only one-time items can upgrade
        if (item.selling_plan_allocation) {
            return;
        }

        const row = document.querySelector(`.miniCartBodyItem[data-key="${item.key}"]`);
        if (!row) {
            return;
        }

        const packSize = parseInt(item.sku.split("-")[1][0]);

        // pack size 5 shows the upgrade (to the 3-pack) for any non-anti-upgrade SKU;
        // other pack sizes also skip the excluded promo variants
        if (packSize === 5) {
            if (!ANTI_UPGRADE_SKUS.includes(item.sku)) {
                row.querySelector(".miniCartBodyItemUpgrade").classList.add("active");
            }
        } else if (!ANTI_UPGRADE_SKUS.includes(item.sku) && !UPGRADE_EXCLUDED_VARIANT_IDS.includes(item.id)) {
            row.querySelector(".miniCartBodyItemUpgrade").classList.add("active");
        }
    });

    // label each active upgrade button with the subscribe-and-save amount
    document.querySelectorAll(".miniCartBodyItemUpgrade.active").forEach(element => {
        const key = element.closest(".miniCartBodyItem").dataset.key;
        const supplement = cartData[key] || {};
        const saveAmount = price(supplement.price - supplement.upgrade.price);
        element.textContent = `Click To Subscribe & Save ${saveAmount}`;
    });
}

/********************* Free Gift Sync *********************/

// remove/adjust free gifts based on the real cart total (excluding _free items). the free-gifts checkout
// extension adds them, since adding here fires an add to cart event for every gift
async function syncFreeGifts() {
    // skip while another sync is in flight; the caller that finishes leaves the cart consistent
    if (syncingFreeGifts) {
        return false;
    }

    // skip if the cart isn't loaded or is empty
    if (!cart || !cart.items || cart.item_count === 0) {
        return false;
    }

    // skip if the free product or free gift variants couldn't be resolved
    const freeProductVariantId = FREE_PRODUCT?.variants?.[0]?.id;
    const freeGiftVariantId = FREE_GIFT?.variants?.[0]?.id;
    if (!freeProductVariantId || !freeGiftVariantId) {
        return false;
    }

    syncingFreeGifts = true;

    try {
        const realItems = cart.items.filter(item => !item.properties?._free);
        const realTotal = realItems.reduce((sum, item) => sum + item.final_line_price, 0);

        const antiHeaderInCart = realItems.some(item => ANTI_HEADER_SKUS.includes(item.sku));
        const antiGiftInCart = realItems.some(item => ANTI_GIFT_SKUS.includes(item.sku));

        // desired free product (whitening strips) quantity
        let desiredFreeProductQty = 0;
        if (FREE_PRODUCT_THRESHOLD && realTotal >= FREE_PRODUCT_THRESHOLD && IS_GIFT_ENABLED_COUNTRY && !antiHeaderInCart) {
            desiredFreeProductQty = 1;
        }

        // desired free gift (velvet bag): present unless an anti-gift SKU is in the cart
        const shouldHaveFreeGift = !antiGiftInCart;

        // current gift state in the cart
        const currentFreeProduct = cart.items.find(item => item.properties?._free && item.variant_id === freeProductVariantId);
        const currentFreeGift = cart.items.find(item => item.properties?._free && item.variant_id === freeGiftVariantId);

        const updates = {};

        // sync the free product (whitening strips)
        if (desiredFreeProductQty > 0) {
            if (currentFreeProduct && currentFreeProduct.quantity !== desiredFreeProductQty) {
                updates[currentFreeProduct.key] = desiredFreeProductQty;
            }
        } else if (currentFreeProduct) {
            updates[currentFreeProduct.key] = 0;
        }

        // sync the free gift (velvet bag)
        if (shouldHaveFreeGift) {
            if (currentFreeGift && currentFreeGift.quantity > 1) {
                updates[currentFreeGift.key] = 1;
            }
        } else if (currentFreeGift) {
            updates[currentFreeGift.key] = 0;
        }

        if (Object.keys(updates).length === 0) {
            return false;
        }

        // bail on an error response so we don't overwrite cart with a status object
        const response = await updateCart({updates});
        if (!response.items) {
            return false;
        }
        cart = response;

        return true;
    } finally {
        syncingFreeGifts = false;
    }
}

/********************* Timer *********************/

// 10-minute cart expiry countdown
function startTimer() {
    timer = setInterval(() => {
        const [mins, secs] = document.querySelector("#miniCartHeaderExpiresTime").innerHTML.split(":");
        let timeMs = (mins * 60000) + (secs * 1000);

        // decrement, or hide the banner and stop at zero
        if (timeMs > 0) {
            timeMs -= 1000;
        } else {
            document.querySelector("#miniCartHeaderExpires").classList.remove("active");
            stopTimer();
        }

        // format back to mm:ss
        const minutes = Math.floor(timeMs / 60000);
        let seconds = ((timeMs % 60000) / 1000).toFixed(0);
        if (seconds < 10) {
            seconds = "0" + seconds;
        }
        document.querySelector("#miniCartHeaderExpiresTime").innerHTML = `${minutes}:${seconds}`;
    }, 1000);
}

function stopTimer() {
    clearInterval(timer);
}

/********************* SavedBy Protection *********************/

// SavedBy shipping protection checkbox.
// When unchecked: a fake button triggers SavedBy's own sb__non-covered-link skip flow.
// This avoids interception issues since SavedBy operates at the cart level, not just button clicks.
(function initSavedByProtectionToggle() {
    function findSkipLink(sbEl) {
        // try light DOM first, then shadow root if open
        return document.querySelector("a.sb__non-covered-link")
            || sbEl?.shadowRoot?.querySelector("a.sb__non-covered-link");
    }

    function injectControls() {
        const sbEl = document.querySelector("savedby-checkout-plus");
        if (!sbEl) {
            return;
        }

        const root = sbEl.shadowRoot || sbEl;

        const infoContainer = root.querySelector(".sb__info-container");
        if (!infoContainer) {
            return;
        }
        if (root.querySelector("#sb-protection-checkbox")) {
            return;
        }

        const checkoutContainer = root.querySelector(".sb__checkout-container");
        if (!checkoutContainer) {
            return;
        }

        const baseStyle = document.createElement("style");
        baseStyle.textContent = ".sb__non-covered-link { display: none !important; } .sb__checkout-container { gap: 0 !important; }";
        root.appendChild(baseStyle);

        infoContainer.insertAdjacentHTML("afterbegin", `
            <label id="sb-protection-checkbox-label" style="display:flex;align-items:center;gap:8px;cursor:pointer;">
                <input type="checkbox" id="sb-protection-checkbox" checked style="width:16px;height:16px;cursor:pointer;">
                <span style="display:none;">Add shipping protection</span>
            </label>
        `);

        checkoutContainer.insertAdjacentHTML("afterbegin", `
            <button id="sb-fake-checkout" style="display:none;max-width:none;width:100%;margin:0;border-radius:15px;text-transform:uppercase;padding:15px;background-color:var(--primary-blue);color:#fff;text-align:center;font-size:18px;font-weight:700;-webkit-appearance:none;appearance:none;cursor:pointer;">Checkout Now</button>
        `);

        const checkbox = infoContainer.querySelector("#sb-protection-checkbox");
        const fakeBtn = root.querySelector("#sb-fake-checkout");

        checkbox.addEventListener("change", function () {
            if (this.checked) {
                root.querySelector("#sb-hide-btn-style")?.remove();
                fakeBtn.style.display = "none";
                return;
            }
            if (!root.querySelector("#sb-hide-btn-style")) {
                const style = document.createElement("style");
                style.id = "sb-hide-btn-style";
                style.textContent = ".sb__button-wrapper { display: none !important; }";
                root.appendChild(style);
            }
            fakeBtn.style.display = "";
        });

        fakeBtn.addEventListener("click", function () {
            const skipLink = findSkipLink(sbEl);
            if (skipLink) {
                skipLink.click();
                return;
            }
            window.location.href = "/checkout";
        });
    }

    injectControls();

    const observer = new MutationObserver(injectControls);
    observer.observe(document.body, {childList: true, subtree: true});
}());

/********************* Init *********************/

// close the cart
function closeCart() {
    document.querySelector("#miniCart").close();
    document.querySelector("body").classList.remove("noScroll");
}

// open / close triggers
document.querySelectorAll(".openCart").forEach(element => {
    element.addEventListener("click", () => {
        openCart();
    });
});
document.querySelectorAll(".closeCart").forEach(element => {
    element.addEventListener("click", closeCart);
});

// close when clicking the dialog backdrop
document.querySelector("#miniCart").addEventListener("click", function (e) {
    if (e.target === this) {
        closeCart();
    }
});

// checkout (the free-gifts checkout extension adds the free gifts)
document.querySelector("#miniCartFooterTopCheckout").addEventListener("click", function () {
    this.classList.add("loading");

    // track jumbleberry initiate checkout
    try {
        jumbleberry("track", "InitiateCheckout");
    } catch (e) {
    }

    window.location.href = "/checkout";
});

// register the dialog polyfill and start the expiry countdown
dialogPolyfill.registerDialog(document.querySelector("#miniCart"));
startTimer();

// auto-open the cart when arriving from the cart page
if (document.referrer === "https://getlivfresh.com/cart") {
    openCart();
}
